<template>
  <div class="dashboard-container">
    <!-- 欢迎卡片 -->
    <el-card class="welcome-card">
      <div class="welcome-content">
        <div class="welcome-left">
          <el-avatar :size="60" :src="userInfo.avatar || defaultAvatar" />
          <div class="welcome-text">
            <h2>{{ greeting }}，{{ userInfo.nickname || userInfo.username }}！</h2>
            <p>{{ today }} · 欢迎使用 {{ siteName }}</p>
          </div>
        </div>
        <div class="welcome-right">
          <div class="stat-item">
            <i class="el-icon-s-custom" />
            <span>管理员</span>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 总量统计卡片：默认一行 6 列（lg=4），屏幕不够时自动降为 4 列（md=6）再到 2 列 -->
    <el-row :gutter="16" class="stat-cards">
      <el-col :xs="12" :sm="12" :md="6" :lg="4" v-for="item in statListWithClick" :key="item.title">
        <el-card
          class="stat-card"
          shadow="hover"
          :class="{ 'stat-card--clickable': item.canClick }"
          @click.native="handleStatClick(item)"
        >
          <div class="stat-card-content" :style="{ background: item.bg }">
            <div class="stat-icon"><i :class="item.icon" /></div>
            <div class="stat-info">
              <div class="stat-num">{{ overview[item.field] != null ? overview[item.field] : '-' }}</div>
              <div class="stat-title">{{ item.title }}</div>
            </div>
            <i v-if="item.canClick" class="stat-arrow el-icon-arrow-right" />
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 今日实时统计 -->
    <el-card class="info-card">
      <div slot="header" class="card-header">
        <span><i class="el-icon-time" /> 今日实时统计</span>
        <el-button type="text" size="mini" icon="el-icon-refresh" :loading="todayLoading" @click="fetchToday(true)">刷新</el-button>
      </div>
      <div class="info-grid">
        <div class="info-item" v-for="item in todayList" :key="item.label">
          <div class="info-num" :style="{ color: item.color }">{{ item.value }}</div>
          <div class="info-label">{{ item.label }}</div>
        </div>
      </div>
    </el-card>

    <!-- 优惠券统计 -->
    <el-card class="info-card">
      <div slot="header" class="card-header">
        <span><i class="el-icon-s-ticket" /> 优惠券统计</span>
      </div>
      <div class="info-grid">
        <div
          v-for="c in couponList"
          :key="c.label"
          class="info-item"
          :class="{ 'info-item--clickable': !!c.onClick }"
          @click="c.onClick && c.onClick()"
        >
          <div class="info-num" :style="{ color: c.color }">{{ c.value }}</div>
          <div class="info-label">
            {{ c.label }}
            <i v-if="c.onClick" class="el-icon-arrow-right info-arrow" />
          </div>
        </div>
      </div>
    </el-card>

    <!-- 趋势折线图 -->
    <el-card class="chart-card">
      <div slot="header" class="card-header">
        <span><i class="el-icon-data-line" /> 近 30 天订单趋势</span>
      </div>
      <div ref="trendChart" class="trend-chart" />
    </el-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as echarts from 'echarts'
import { getStatisticsOverview, getStatisticsToday, getStatisticsCharts } from '@/api/dashboard'

const PIE_COLORS = ['#667eea', '#409eff', '#67c23a', '#e6a23c', '#36cfc9', '#f56c6c', '#9b6bff', '#5cdbd3']

export default {
  name: 'Dashboard',
  data() {
    return {
      defaultAvatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
      overview: {},
      todayStats: {},
      todayLoading: false,
      charts: null,
      trendChart: null,
      salesChart: null,
      statusChart: null,
      statList: [
        { title: '总商品订单数', field: 'product_order_total', icon: 'el-icon-shopping-bag-1', bg: 'linear-gradient(135deg,#667eea,#9b6bff)', permission: 'order:product:list', path: '/order/product' },
        { title: '总服务订单数', field: 'service_order_total', icon: 'el-icon-s-management',   bg: 'linear-gradient(135deg,#409eff,#53c8ff)', permission: 'order:service:list', path: '/order/service' },
        { title: '总客户数',     field: 'user_total',          icon: 'el-icon-user-solid',      bg: 'linear-gradient(135deg,#67c23a,#95d475)', permission: 'member:list',            path: '/member/index' },
        { title: '总合伙人数',   field: 'partner_total',       icon: 'el-icon-s-cooperation',   bg: 'linear-gradient(135deg,#e6a23c,#f0c27f)', permission: 'partner:list',           path: '/partner/index' },
        { title: '总用户数', field: 'order_contact_total', icon: 'el-icon-user',            bg: 'linear-gradient(135deg,#36cfc9,#5cdbd3)', permission: 'order:orderContact:list', path: '/order/order-contact' },
        { title: '申请退款订单', field: 'refund_applied_total',icon: 'el-icon-warning-outline',  bg: 'linear-gradient(135deg,#f56c6c,#f89898)', permission: 'order:product:list',      path: '/order/product', query: { refund_apply_status: 1 } }
      ]
    }
  },
  computed: {
    ...mapGetters(['userInfo', 'permissions', 'roles', 'siteName']),
    greeting() {
      const h = new Date().getHours()
      if (h < 6) return '深夜好'
      if (h < 12) return '早上好'
      if (h < 14) return '中午好'
      if (h < 18) return '下午好'
      return '晚上好'
    },
    today() {
      const d = new Date()
      return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 星期${'日一二三四五六'[d.getDay()]}`
    },
    statListWithClick() {
      return this.statList.map(item => ({ ...item, canClick: this.hasPerm(item.permission) }))
    },
    todayList() {
      const t = this.todayStats || {}
      return [
        { label: '今日新增客户',   value: t.new_user_count          ?? '-', color: '#67c23a' },
        { label: '今日商品订单',   value: t.new_product_order_count  ?? '-', color: '#409eff' },
        { label: '今日支付金额(元)', value: t.product_pay_amount != null ? Number(t.product_pay_amount).toFixed(2) : '-', color: '#9b6bff' },
        { label: '今日服务订单',   value: t.new_service_order_count  ?? '-', color: '#36cfc9' }
      ]
    },
    couponList() {
      const c = (this.overview || {}).coupon || {}
      const canNavMember = this.hasPerm('member:list')
      return [
        { label: '已发放',   value: c.issued_total        ?? '-', color: '#409eff' },
        { label: '已使用',   value: c.used_total           ?? '-', color: '#67c23a' },
        { label: '使用率',   value: c.usage_rate != null ? c.usage_rate.toFixed(1) + '%' : '-', color: '#e6a23c' },
        { label: '即将到期', value: c.expiring_soon        ?? '-', color: '#f56c6c' },
        {
          label: '待发放',
          value: c.pending_issue_total ?? '-',
          color: '#9b6bff',
          onClick: canNavMember
            ? () => this.$router.push({ path: '/member/index', query: { pending_coupon_issue: '1' } })
            : null
        }
      ]
    }
  },
  async created() {
    this.fetchOverview()
    this.fetchToday()
    this.fetchCharts()
  },
  mounted() {
    window.addEventListener('resize', this.resizeCharts)
  },
  activated() {
    this.$nextTick(() => this.resizeCharts())
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCharts)
    ;[this.trendChart, this.salesChart, this.statusChart].forEach(c => c && c.dispose())
  },
  methods: {
    hasPerm(permission) {
      if (!permission) return true
      return (this.roles || []).includes('super_admin') || (this.permissions || []).includes(permission)
    },
    handleStatClick(item) {
      if (!this.hasPerm(item.permission)) return
      this.$router.push({ path: item.path, query: item.query || {} })
    },

    async fetchOverview() {
      try {
        const res = await getStatisticsOverview()
        this.overview = res.data || {}
      } catch (e) { /* ignore */ }
    },

    async fetchToday(forceRefresh = false) {
      this.todayLoading = true
      try {
        const res = await getStatisticsToday({ force_refresh: forceRefresh })
        this.todayStats = res.data || {}
      } catch (e) { /* ignore */ }
      finally { this.todayLoading = false }
    },

    async fetchCharts() {
      try {
        const res = await getStatisticsCharts()
        const d = res.data || {}
        this.$nextTick(() => {
          this.renderTrendChart(d.daily_trends || [])
        })
      } catch (e) { /* ignore */ }
    },

    // ─── 趋势折线图 ────────────────────────────────────────────────
    renderTrendChart(dailyTrends) {
      if (!this.$refs.trendChart) return
      if (!this.trendChart) this.trendChart = echarts.init(this.$refs.trendChart)
      const dates         = dailyTrends.map(d => d.date)
      const productCounts = dailyTrends.map(d => d.product_order_count)
      const serviceCounts = dailyTrends.map(d => d.service_order_count)
      const payAmounts    = dailyTrends.map(d => +(d.pay_amount || 0).toFixed(2))
      const newUsers      = dailyTrends.map(d => d.new_user_count)
      this.trendChart.setOption({
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' },
          formatter(params) {
            let html = `<div style="font-weight:600;margin-bottom:4px">${params[0].axisValue}</div>`
            params.forEach(p => {
              const unit = p.seriesName === '支付金额(元)' ? '元' : '个'
              html += `<div style="display:flex;align-items:center;gap:5px;margin:2px 0">
                <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color}"></span>
                ${p.seriesName}：<b>${p.value}</b> ${unit}
              </div>`
            })
            return html
          }
        },
        legend: { data: ['商品订单数', '支付金额(元)'], top: 8, right: 16, itemWidth: 14, textStyle: { fontSize: 12 } },
        grid: { left: 40, right: 90, bottom: 40, top: 64, containLabel: true },
        xAxis: { type: 'category', data: dates, boundaryGap: false, axisLabel: { rotate: 30, fontSize: 11, formatter: v => v.slice(5) } },
        yAxis: [
          { type: 'value', name: '数量', nameTextStyle: { color: '#909399', fontSize: 11 }, minInterval: 1, axisLabel: { fontSize: 11 } },
          { type: 'value', name: '金额(元)', nameTextStyle: { color: '#909399', fontSize: 11 }, axisLabel: { fontSize: 11, formatter: v => v >= 10000 ? (v / 10000).toFixed(1) + 'w' : v } }
        ],
        series: [
          { name: '商品订单数', type: 'line', smooth: true, yAxisIndex: 0, data: productCounts, symbol: 'circle', symbolSize: 5, lineStyle: { width: 2, color: '#667eea' }, itemStyle: { color: '#667eea' }, areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: 'rgba(102,126,234,0.2)' }, { offset: 1, color: 'rgba(102,126,234,0)' }]) } },
          { name: '支付金额(元)', type: 'bar', yAxisIndex: 1, data: payAmounts, barMaxWidth: 18, itemStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: '#409eff' }, { offset: 1, color: '#a0cfff' }]), borderRadius: [3, 3, 0, 0] } }
        ]
      })
    },

    resizeCharts() {
      ;[this.trendChart, this.salesChart, this.statusChart].forEach(c => c && c.resize())
    }
  }
}
</script>

<style lang="scss" scoped>
.dashboard-container {
  padding: 20px;

  // ── 欢迎卡片 ────────────────────────────────────────────────────
  .welcome-card {
    margin-bottom: 16px;
    .welcome-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      .welcome-left {
        display: flex;
        align-items: center;
        gap: 16px;
        .welcome-text {
          h2 { margin: 0 0 5px; font-size: 18px; color: #303133; }
          p  { margin: 0; color: #909399; font-size: 13px; }
        }
      }
      .welcome-right .stat-item {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #409eff;
        font-size: 14px;
        i { font-size: 18px; }
      }
    }
  }

  // ── 总量统计卡片 ────────────────────────────────────────────────
  .stat-cards {
    margin-bottom: 4px;
    .el-col { margin-bottom: 16px; }
    .stat-card {
      border-radius: 10px;
      overflow: hidden;
      // 去掉 el-card 默认 body padding，由 stat-card-content 自己控制
      ::v-deep .el-card__body { padding: 0; }
      &--clickable {
        cursor: pointer;
        transition: transform 0.2s, box-shadow 0.2s;
        &:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          .stat-arrow { opacity: 1; transform: translateY(-50%) translateX(2px); }
        }
      }
      .stat-card-content {
        display: flex;
        align-items: center;
        gap: 12px;
        border-radius: 8px;
        padding: 16px 14px;
        position: relative;
        .stat-icon {
          flex-shrink: 0;
          width: 50px;
          height: 50px;
          background: rgba(255,255,255,0.22);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          i { font-size: 24px; color: #fff; }
        }
        .stat-info {
          flex: 1;
          min-width: 0;
          .stat-num   { font-size: 26px; font-weight: 700; color: #fff; line-height: 1; }
          .stat-title { font-size: 12px; color: rgba(255,255,255,0.88); margin-top: 5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        }
        .stat-arrow {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(255,255,255,0.7);
          font-size: 15px;
          opacity: 0;
          transition: opacity 0.2s, transform 0.2s;
        }
      }
    }
  }

  // ── 今日实时 / 优惠券统计（统一样式）──────────────────────────
  .info-card {
    margin-bottom: 16px;

    .card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 15px;
      font-weight: 600;
      color: #303133;
      i { margin-right: 6px; }
    }

    .info-grid {
      display: flex;
      .info-item {
        flex: 1;
        text-align: center;
        padding: 14px 0;
        border-right: 1px solid #f0f0f0;
        &:last-child { border-right: none; }
        .info-num   { font-size: 28px; font-weight: 700; line-height: 1; }
        .info-label {
          font-size: 12px;
          color: #909399;
          margin-top: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 3px;
          .info-arrow { font-size: 11px; opacity: 0.6; }
        }
        &--clickable {
          cursor: pointer;
          transition: background 0.18s;
          border-radius: 6px;
          &:hover {
            background: rgba(155, 107, 255, 0.06);
            .info-label { color: #9b6bff; }
            .info-arrow { opacity: 1; }
          }
        }
      }
    }
  }

  // ── 图表卡片公共样式 ────────────────────────────────────────────
  .chart-card {
    margin-bottom: 16px;
    .card-header {
      font-size: 15px;
      font-weight: 600;
      color: #303133;
      i { margin-right: 6px; }
    }
    .trend-chart { width: 100%; height: 320px; }
    .pie-chart   { width: 100%; height: 280px; }
  }

  .bottom-charts .el-col { margin-bottom: 0; }
}

// ── 小屏响应式 ────────────────────────────────────────────────────
@media (max-width: 768px) {
  .dashboard-container {
    padding: 12px 10px;

    // 欢迎卡片：竖排
    .welcome-card .welcome-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
      .welcome-left h2 { font-size: 15px; }
      .welcome-left p  { font-size: 12px; }
      .welcome-right   { display: none; }
    }

    // stat 卡片：压缩内边距与字号
    .stat-cards {
      .el-col { margin-bottom: 10px; }
      .stat-card .stat-card-content {
        padding: 12px 10px;
        gap: 8px;
        .stat-icon {
          width: 38px;
          height: 38px;
          i { font-size: 18px; }
        }
        .stat-info {
          .stat-num   { font-size: 20px; }
          .stat-title { font-size: 11px; }
        }
        .stat-arrow { right: 6px; font-size: 12px; }
      }
    }

    // 今日统计 / 优惠券统计：每行 2 项
    .info-card .info-grid {
      flex-wrap: wrap;
      .info-item {
        flex: 0 0 50%;
        border-right: none;
        border-bottom: 1px solid #f0f0f0;
        padding: 12px 0;
        // 奇数列右侧加分隔线
        &:nth-child(odd)  { border-right: 1px solid #f0f0f0; }
        // 最后两项去掉底部线
        &:nth-last-child(-n+2) { border-bottom: none; }
        // 如果最后一项是奇数位（总数为奇数），让它跨满
        &:last-child:nth-child(odd) {
          flex: 0 0 100%;
          border-right: none;
        }
        .info-num { font-size: 22px; }
      }
    }
  }
}
</style>
